import { PlusIcon, User, XIcon } from 'lucide-react';
import { useRef, useState } from 'react';

import {
  type QueryFilterExpressionUnit,
  type QueryFilterExpressionValue,
  LogicalOperator,
  QUERY_FILTER_TYPE,
} from '@/entities/query/query-types';
import { Button } from '@/shared/components/shadcn/button';
import { Input } from '@/shared/components/shadcn/input';
import { Label } from '@/shared/components/shadcn/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/shadcn/select';
import { cn } from '@/shared/utils/cn';

interface FilterCardProps {
  className?: string;
  title?: string;
  type: 'include' | 'exclude';
  data: QueryFilterExpressionUnit;
  onChange: (data: QueryFilterExpressionUnit) => void;
  onDelete?: () => void;
  showDeleteButton?: boolean;
}

function getFilterValuePlaceholder(filterValueType: QUERY_FILTER_TYPE) {
  switch (filterValueType) {
    case QUERY_FILTER_TYPE.WORD:
      return 'word, phrase, #hashtag';
    case QUERY_FILTER_TYPE.USERNAME:
      return 'username';
    default:
      console.error(`Invalid filter value type: ${filterValueType}`);
      return 'unknown';
  }
}

export function Filter({
  className,
  title,
  type,
  data,
  onChange,
  onDelete,
  showDeleteButton = false,
}: FilterCardProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [filterValueType, setFilterValueType] = useState(QUERY_FILTER_TYPE.WORD);
  const [inputValue, setInputValue] = useState('');

  const isInclude = type === 'include';
  const label = isInclude ? 'Include' : 'Exclude';

  // Convert data to UI representation
  const filterOperator = data.op === LogicalOperator.And ? 'all of' : 'any of';

  // Always show at least one input field
  const values = data.values || [];

  const handleFilterTypeChange = (newFilterType: string) => {
    const op = newFilterType === 'all of' ? LogicalOperator.And : LogicalOperator.Or;
    onChange({
      ...data,
      op,
    });
  };

  const removeKeyword = (index: number) => {
    const newValues = values.filter((_, i) => i !== index);
    onChange({
      ...data,
      values: newValues as QueryFilterExpressionValue[],
    });
  };

  const addKeyword = (newValue: QueryFilterExpressionValue) => {
    onChange({
      ...data,
      op: data.op || LogicalOperator.Or,
      values: [...values, newValue],
    });
    setInputValue('');
  };

  return (
    <div className={cn('relative flex flex-col gap-2 space-y-2 border p-6', className)}>
      {title && <Label className="text-foreground mb-6 text-base font-medium">{title}</Label>}
      {showDeleteButton && (
        <Button variant="ghost" size="sm" onClick={onDelete} className="absolute top-2 right-2">
          <XIcon className="size-4" />
        </Button>
      )}

      <div className="flex items-center gap-2">
        <Label className="text-foreground text-sm font-medium">{label}</Label>
        <Select value={filterOperator} onValueChange={handleFilterTypeChange}>
          <SelectTrigger className="bg-muted h-9 w-auto border-transparent font-medium">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="any of">any of</SelectItem>
            {isInclude && <SelectItem value="all of">all of</SelectItem>}
          </SelectContent>
        </Select>
      </div>

      <div className="flex flex-wrap gap-2">
        {values?.map((value, index) => (
          <div
            key={index}
            className={cn(
              'flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-semibold',
              type === 'include' && 'bg-blue-50 text-blue-600',
              type === 'exclude' && 'bg-red-50 text-red-600',
            )}
          >
            {value.type === QUERY_FILTER_TYPE.USERNAME && <User className="size-4" />}
            {value.value}
            <XIcon className="size-4 cursor-pointer" onClick={() => removeKeyword(index)} />
          </div>
        ))}
      </div>

      <div className="flex items-center gap-2">
        <Select
          value={filterValueType}
          onValueChange={(value) => setFilterValueType(value as QUERY_FILTER_TYPE)}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={QUERY_FILTER_TYPE.WORD}>Keyword</SelectItem>
            <SelectItem value={QUERY_FILTER_TYPE.USERNAME}>Account</SelectItem>
          </SelectContent>
        </Select>
        <Input
          className="h-10 px-3"
          ref={inputRef}
          placeholder={getFilterValuePlaceholder(filterValueType)}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              const currentValue = event.currentTarget.value;
              const isEmpty = currentValue === '';
              event.preventDefault();

              // Don't do anything if current input is empty
              if (isEmpty) {
                return;
              }

              addKeyword({ type: filterValueType, value: inputValue });
            }
          }}
        />
        <Button
          variant="default"
          disabled={!inputValue}
          onClick={() => addKeyword({ type: filterValueType, value: inputValue })}
          size="lg"
          className="size-10 p-0"
        >
          <PlusIcon className="size-4" />
        </Button>
      </div>
    </div>
  );
}
